import { Link } from "@tanstack/react-router";
import { Phone, Menu, X, Facebook, MessageSquare, ArrowRight, ChevronDown } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Logo } from "@/components/Logo";
import { track } from "@/lib/analytics";
import { SiteSearch } from "@/components/SiteSearch";
import { SeriesBadge } from "@/components/SeriesBadge";
import { trackBadgeClick, trackSeriesCta } from "@/lib/analytics";
import { seriesProductCount } from "@/data/series-products";
import {
  desktopFbLink,
  desktopFbLinkActive,
  desktopNavLink,
  desktopNavLinkActive,
  mobileFbLink,
  mobileFbLinkActive,
  mobileNavLink,
  mobileNavLinkActive,
  NAV_FOCUS_RING,
  TOPBAR_FOCUS_RING,
} from "@/styles/nav";

/**
 * localStorage key tracking whether the user has clicked the FB header link
 * before. On return visits we apply the same active styling that route-active
 * nav links use, so the FB link visually rejoins the rest of the nav.
 */
const FB_VISITED_KEY = "tms.fbHeaderVisited";

const FB_URL = "https://www.facebook.com/profile.php?id=61556233022173";
const FB_URL_DESKTOP = `${FB_URL}&utm_source=site&utm_medium=header&utm_campaign=day_to_day&utm_content=header_desktop`;
const FB_URL_MOBILE = `${FB_URL}&utm_source=site&utm_medium=header&utm_campaign=day_to_day&utm_content=header_mobile`;

type NavChild = { to: string; label: string; children?: NavChild[] };
type NavItem = { to: string; label: string; children?: NavChild[] };

type SeriesMegaEntry = { slug: string; to: string; name: string; description: string };
type SeriesMegaGroup = { title: string; entries: SeriesMegaEntry[] };

const SERIES_MEGA: SeriesMegaGroup[] = [
  {
    title: "Residential & Outdoor Living",
    entries: [
      { slug: "alamo-series", to: "/series/alamo-series", name: "Alamo Series", description: "Residential Awnings & Shade" },
      { slug: "hill-country-series", to: "/hill-country-series", name: "Hill Country Series", description: "Outdoor Living & Premium Shade" },
    ],
  },
  {
    title: "Commercial, Hospitality & Institutional",
    entries: [
      { slug: "longhorn-series", to: "/series/longhorn-series", name: "Longhorn Series", description: "Commercial Shade Systems" },
      { slug: "rio-grande-series", to: "/series/rio-grande-series", name: "Rio Grande Series", description: "Hospitality & Resorts" },
      { slug: "republic-series", to: "/series/republic-series", name: "Republic Series", description: "Government, Schools & Churches" },
      { slug: "bluebonnet-series", to: "/bluebonnet-series", name: "Bluebonnet Series", description: "Tents, Vendor Booths & Outdoor Markets" },
    ],
  },
  {
    title: "Ag, Ranch & Recreation",
    entries: [
      { slug: "farming-ranching-series", to: "/farming-ranching-series", name: "Farming & Ranching Series", description: "Agricultural Covers, Shade & Fabrication" },
      { slug: "outdoor-recreation-series", to: "/outdoor-recreation-series", name: "Outdoor Recreation Series", description: "Hunting, Fishing & Boating" },
    ],
  },
  {
    title: "Specialty, Service & Automation",
    entries: [
      { slug: "revival-series", to: "/series/revival-series", name: "Revival Series", description: "Service & Recovery" },
      { slug: "lone-star-elite", to: "/series/lone-star-elite", name: "Lone Star Series", description: "Smart Lighting & Automation" },
      { slug: "trail-boss", to: "/series/trail-boss", name: "Trail Boss", description: "Welding & Custom Fabrication" },
      { slug: "ranger-care", to: "/series/ranger-care", name: "Ranger Care", description: "Maintenance Memberships" },
    ],
  },
];

const SERIES_FLAT: NavChild[] = SERIES_MEGA.flatMap((g) =>
  g.entries.map((e) => ({ to: e.to, label: `${e.name} — ${e.description}` })),
);

const nav: NavItem[] = [
  { to: "/", label: "Home" },
  {
    to: "/series",
    label: "Series",
    children: [...SERIES_FLAT, { to: "/series", label: "View All Series →" }],
  },
  {
    to: "/products",
    label: "Products",
    children: [
      { to: "/products", label: "All Products — Overview" },

      {
        to: "/awnings",
        label: "Awnings",
        children: [
          { to: "/custom-awnings", label: "Custom Awnings" },
          { to: "/residential-awnings", label: "Residential Awnings" },
          { to: "/commercial-awnings", label: "Commercial Awnings" },
          { to: "/restaurant-awnings", label: "Restaurant Awnings" },
          { to: "/retractable-awnings", label: "Solair® Retractable Awnings" },
          { to: "/solair-vs", label: "— Solair® vs. The Rest" },
          { to: "/metal-awnings", label: "Metal Awnings" },
          { to: "/fabric-awnings", label: "Fabric Awnings" },
          { to: "/backlit-awnings", label: "Backlit Awnings" },

        ],
      },
      {
        to: "/hill-country-series",
        label: "Outdoor Comfort",
        children: [
          { to: "/retractable-awnings", label: "Retractable Awnings" },
          { to: "/exterior-vertical-shades", label: "Motorized Screens" },
          { to: "/louvered-pergolas", label: "Pergolas and Patio Covers" },
          { to: "/outdoor-lighting", label: "Outdoor Lighting" },
          { to: "/outdoor-hvac", label: "Outdoor Heating & Cooling" },
          { to: "/outdoor-cooling-misting", label: "Outdoor Misting Cooling" },
          { to: "/safari-glamping-tents", label: "Glamping & Safari Tents" },
          { to: "/curtains", label: "Patio Shades & Curtains" },
          { to: "/sail-shades", label: "Sail Shades" },
          { to: "/lone-star-elite", label: "Smart Automation" },
          { to: "/hill-country-series", label: "View All Outdoor Comfort →" },
        ],
      },
      {
        to: "/curtains",
        label: "Screens & Curtains",
        children: [
          { to: "/exterior-vertical-shades", label: "Motorized Patio Screens" },
          { to: "/curtains", label: "Outdoor Curtains" },
          { to: "/gazebo-privacy-panels", label: "Gazebo Covers & Privacy Panels" },
        ],
      },
      {
        to: "/commercial-event-tents",
        label: "Tents & Event Canopies",
        children: [
          { to: "/commercial-event-tents", label: "Commercial Event Tents" },
          { to: "/commercial-event-tents#pop-up-tents", label: "Pop-Up Tents" },
          { to: "/commercial-event-tents#custom-printed-tent-tops", label: "Custom-Printed Tent Tops" },
          { to: "/commercial-event-tents#tent-sidewalls", label: "Tent Sidewalls" },
          { to: "/commercial-event-tents#vendor-and-farmers-market-tents", label: "Vendor and Farmers-Market Tents" },
          { to: "/bluebonnet-series", label: "Tents, Vendor Booths & Markets" },
          { to: "/recover-awnings", label: "Tent Repair & Recover" },
          { to: "/safari-glamping-tents", label: "Glamping & Safari Tents" },
        ],
      },


      {
        to: "/sail-shades",
        label: "Structures",
        children: [
          { to: "/sail-shades", label: "Sail Shades & Tension Structures" },
          { to: "/louvered-pergolas", label: "Motorized Louvered Pergolas" },
          { to: "/pergola-covers", label: "Pergola Covers" },
          { to: "/patio-covers", label: "Patio Covers" },
          { to: "/pool-spa", label: "Pool & Spa Shade" },
          { to: "/covered-parking", label: "Covered Parking" },
          { to: "/playground-shade", label: "Playground Shade" },
          { to: "/school-shade-structures", label: "School Shade Structures" },
          { to: "/church-shade-structures", label: "Church Shade Structures" },
          { to: "/car-wash-shade-sails", label: "Car Wash Shade Sails" },
        ],
      },
      {
        to: "/covers-tops",
        label: "Covers & Fabrication",
        children: [
          { to: "/boat-covers", label: "Boat Covers" },
          { to: "/rv-covers", label: "RV Covers" },
          { to: "/wagon-covers", label: "Wagon Covers" },
          { to: "/stock-trailer-covers", label: "Stock Trailer Covers" },
          { to: "/trailer-covers", label: "Trailer Covers" },
          { to: "/hunting-fishing-covers", label: "Hunting & Fishing Covers" },
          { to: "/deer-blind-covers", label: "Deer Blind Covers" },
          { to: "/cooler-gun-gear-covers", label: "Cooler / Gun / Gear Covers" },
          { to: "/dog-boxes", label: "Dog Boxes" },
          { to: "/hog-traps-and-cages", label: "Hog Traps & Cages" },
          { to: "/custom-fabrication", label: "Welding & Custom Fabrication" },
          { to: "/trailer-wiring", label: "Trailer Wiring" },
        ],
      },
    ],
  },
  {
    to: "/revival-series",
    label: "Service & Care",
    children: [
      { to: "/revival-series", label: "Revival — Service & Recovery" },
      { to: "/ranger-care", label: "Ranger Care Memberships" },
      { to: "/recover-awnings", label: "Awning & Canopy Recover" },
      { to: "/custom-fabrication", label: "Welding & Frame Repair" },
      { to: "/trailer-wiring", label: "Trailer Wiring" },
      { to: "/estimate-quotes#general", label: "Request Service" },
    ],
  },
  { to: "/projects", label: "Projects" },
  {
    to: "/blog",
    label: "Resources",
    children: [
      { to: "/blog", label: "Learning Center / Blog" },
      { to: "/faqs", label: "FAQs" },
      { to: "/local-faqs", label: "Texas Service Area FAQs" },
      { to: "/fabrics", label: "Fabrics" },
      { to: "/find-your-shade", label: "Find Your Shade (Quiz)" },
      { to: "/built-in-texas", label: "Built In Texas — Our Shop" },
      { to: "/why-texas-made-shade", label: "Why Texas Made Shade" },
      { to: "/architects", label: "For Architects & Specifiers" },
      { to: "/shop", label: "Shop" },
    ],
  },
  {
    to: "/contact",
    label: "About",
    children: [
      { to: "/contact", label: "About & Contact" },
      { to: "/projects", label: "Project Gallery" },
      { to: "/case-studies", label: "Case Studies" },
      { to: "/central-texas", label: "Central Texas" },
      { to: "/hill-country", label: "Hill Country" },
      { to: "/gulf-coast", label: "Gulf Coast" },
    ],
  },
  {
    to: "/estimate-quotes",
    label: "Get a Quote",
    children: [
      { to: "/estimate-quotes#general", label: "General / Other Project" },
      { to: "/estimate-quotes#wagon-cover", label: "Wagon Cover" },
      { to: "/estimate-quotes#drop-curtain", label: "Drop Curtain" },
      { to: "/estimate-quotes#boat-cover", label: "Boat Cover" },
      { to: "/estimate-quotes#rv-cover", label: "RV Cover" },
      { to: "/estimate-quotes#gazebo-cover", label: "Gazebo / Pergola Cover" },
      { to: "/estimate-quotes#stock-trailer", label: "Stock Trailer Cover" },
      { to: "/estimate-quotes#hunting-tarp", label: "Hunting Tarp / Blind Cover" },
      { to: "/estimate-quotes#cooler-cushion", label: "Cooler Cushion / Gear Cover" },
      { to: "/estimate-quotes#dog-box", label: "Dog Box / Hunting Truck" },
    ],
  },
];


/**
 * Desktop nav dropdown with full keyboard accessibility:
 * - Trigger has aria-haspopup="menu" + aria-expanded
 * - ArrowDown/Enter/Space on trigger opens menu and focuses first item
 * - ArrowUp/Down move between items, Home/End jump to first/last
 * - Esc closes and returns focus to trigger
 * - Tab moves naturally to next/previous focusable element (closes the menu)
 * - Mouse hover still opens the menu
 */
function DesktopNavDropdown({ item }: { item: NavItem }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const closeTimer = useRef<number | null>(null);
  const menuId = useId();

  const children = item.children ?? [];
  const isSeries = item.label === "Series";
  // The Series mega panel renders one menuitem per series entry plus the
  // trailing "View All Series" link, so its roving-focus count differs from
  // the flat children list used by the simple dropdowns.
  const itemCount = isSeries ? SERIES_FLAT.length + 1 : children.length;

  const clearCloseTimer = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  // Focus is applied in an effect (not inline) because the panel is
  // visibility-hidden while closed — focusing before React commits the open
  // state would be a no-op.
  const [pendingFocus, setPendingFocus] = useState<number | null>(null);

  const focusItem = useCallback((index: number) => {
    const count = itemCount;
    if (count === 0) return;
    setPendingFocus((index + count) % count);
  }, [itemCount]);

  useEffect(() => {
    if (!open || pendingFocus === null) return;
    const el = itemRefs.current[pendingFocus];
    setPendingFocus(null);
    el?.focus();
  }, [open, pendingFocus]);

  const openMenu = useCallback((focusIndex?: number) => {
    setOpen(true);
    if (typeof focusIndex === "number") focusItem(focusIndex);
  }, [focusItem]);


  const closeMenu = useCallback((returnFocus = false) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Close when focus leaves the whole dropdown (Tabbing out).
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setOpen(false);
  };

  // Esc closes and returns focus to trigger.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeMenu]);

  // Size the Series mega panel to the space available to the right of its
  // trigger so the first column stays aligned with the "Series" link. Only if
  // that space is too tight for three columns do we shift the panel left.
  const [megaStyle, setMegaStyle] = useState<{ width?: number; shift: number }>({ shift: 0 });
  useEffect(() => {
    if (!open || item.label !== "Series") return;
    const reposition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const gutter = 16;
      const maxWidth = 1152; // 72rem
      const minWidth = 720; // below this, 3 columns get too cramped
      const triggerLeft = trigger.getBoundingClientRect().left;
      const available = window.innerWidth - triggerLeft - gutter;
      if (available >= minWidth) {
        setMegaStyle({ width: Math.min(maxWidth, available), shift: 0 });
        return;
      }
      const width = Math.min(maxWidth, window.innerWidth - gutter * 2);
      const desiredLeft = Math.max(gutter, window.innerWidth - width - gutter);
      setMegaStyle({ width, shift: desiredLeft - triggerLeft });
    };
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [open, item.label]);

  const onTriggerKeyDown = (e: ReactKeyboardEvent<HTMLAnchorElement>) => {
    switch (e.key) {
      case "ArrowDown":
      case "Enter":
      case " ":
        e.preventDefault();
        openMenu(0);
        break;
      case "ArrowUp":
        e.preventDefault();
        openMenu(itemCount - 1);
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          closeMenu(true);
        }
        break;
    }
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const active = document.activeElement as HTMLElement | null;
    const idx = itemRefs.current.findIndex((el) => el === active);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusItem(idx + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusItem(idx - 1);
        break;
      case "Home":
        e.preventDefault();
        focusItem(0);
        break;
      case "End":
        e.preventDefault();
        focusItem(itemCount - 1);
        break;
      case "Tab":
        // Allow natural tab order; closing on blur handles state.
        setOpen(false);
        break;
    }
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => { clearCloseTimer(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      onBlur={handleBlur}
    >
      <Link
        ref={triggerRef}
        to={item.to}
        className={desktopNavLink}
        activeProps={{ className: desktopNavLinkActive, "aria-current": "page" }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="inline-flex items-center gap-1">
          {item.label}
          <ChevronDown className="h-3 w-3 opacity-70" aria-hidden="true" focusable="false" />
        </span>
      </Link>
      {isSeries ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Series submenu"
          onKeyDown={onMenuKeyDown}
          style={{
            transform: `translateX(${megaStyle.shift}px)`,
            width: megaStyle.width ? `${megaStyle.width}px` : undefined,
          }}
          className={`absolute left-0 top-full z-50 max-h-[calc(100vh-8rem)] w-[min(72rem,calc(100vw-2rem))] overflow-y-auto border-t-2 border-red bg-navy-deep px-5 py-6 shadow-2xl transition-opacity duration-150 ${open ? "visible opacity-100" : "invisible opacity-0"}`}
        >
          <SeriesMegaPanel
            open={open}
            onSelect={() => setOpen(false)}
            registerRef={(el, i) => { itemRefs.current[i] = el; }}
          />
        </div>
      ) : (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={`${item.label} submenu`}
          onKeyDown={onMenuKeyDown}
          className={`absolute left-0 top-full z-50 min-w-[16rem] border-t-2 border-red bg-navy-deep shadow-xl transition-opacity duration-150 ${open ? "visible opacity-100" : "invisible opacity-0"}`}
        >
          {children.map((c, i) => (
            <NestedMenuItem
              key={c.to}
              child={c}
              innerRef={(el) => { itemRefs.current[i] = el; }}
              open={open}
              onSelect={() => setOpen(false)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NestedMenuItem({
  child,
  innerRef,
  open,
  onSelect,
}: {
  child: NavChild;
  innerRef: (el: HTMLAnchorElement | null) => void;
  open: boolean;
  onSelect: () => void;
}) {
  const [subOpen, setSubOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const hasChildren = !!child.children && child.children.length > 0;

  const clearTimer = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    clearTimer();
    closeTimer.current = window.setTimeout(() => setSubOpen(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => { clearTimer(); if (hasChildren) setSubOpen(true); }}
      onMouseLeave={hasChildren ? scheduleClose : undefined}
    >
      <Link
        to={child.to}
        ref={innerRef}
        role="menuitem"
        tabIndex={open ? 0 : -1}
        aria-haspopup={hasChildren ? "menu" : undefined}
        aria-expanded={hasChildren ? subOpen : undefined}
        className={`flex items-center justify-between gap-2 px-4 py-2.5 font-display text-xs uppercase tracking-wider text-white/85 hover:bg-red hover:text-white ${NAV_FOCUS_RING}`}
        activeProps={{ className: "flex items-center justify-between gap-2 px-4 py-2.5 font-display text-xs uppercase tracking-wider bg-red text-white", "aria-current": "page" }}
        onClick={onSelect}
      >
        <span>{child.label}</span>
        {hasChildren && <ChevronDown className="h-3 w-3 -rotate-90 opacity-70" aria-hidden="true" focusable="false" />}
      </Link>
      {hasChildren && (
        <div
          role="menu"
          aria-label={`${child.label} submenu`}
          className={`absolute left-full top-0 z-50 min-w-[18rem] border-l-2 border-red bg-navy-deep shadow-xl transition-opacity duration-150 ${subOpen ? "visible opacity-100" : "invisible opacity-0"}`}
        >
          {child.children!.map((gc) => (
            <Link
              key={gc.to}
              to={gc.to}
              role="menuitem"
              tabIndex={subOpen ? 0 : -1}
              className={`block px-4 py-2.5 font-display text-xs uppercase tracking-wider text-white/85 hover:bg-red hover:text-white ${NAV_FOCUS_RING}`}
              activeProps={{ className: "block px-4 py-2.5 font-display text-xs uppercase tracking-wider bg-red text-white", "aria-current": "page" }}
              onClick={() => { setSubOpen(false); onSelect(); }}
            >
              {gc.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function SeriesMegaPanel({
  open,
  onSelect,
  registerRef,
}: {
  open: boolean;
  onSelect: () => void;
  registerRef: (el: HTMLAnchorElement | null, index: number) => void;
}) {
  let idx = 0;
  return (
    <div className="grid gap-x-5 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
      {SERIES_MEGA.map((group) => (
        <div key={group.title} role="group" aria-label={group.title} className="flex flex-col">
          <div className="mb-3 flex items-center gap-2 border-b border-white/15 pb-2 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-red">
            <span className="h-3 w-0.5 shrink-0 bg-red" aria-hidden="true" />
            {group.title}
          </div>
          <ul className="flex flex-col gap-1.5" role="presentation">
            {group.entries.map((entry) => {
              const currentIdx = idx++;
              const count = seriesProductCount(entry.slug);
              return (
                <li key={entry.slug} role="presentation">
                  <Link
                    to={entry.to}
                    ref={(el) => registerRef(el, currentIdx)}
                    role="menuitem"
                    tabIndex={open ? 0 : -1}
                    onClick={() => {
                      trackBadgeClick(
                        { slug: entry.slug, name: entry.name, placement: "mega_menu" },
                        entry.to,
                      );
                      onSelect();
                    }}
                    className={`group flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-left transition-colors hover:border-red hover:bg-red/15 focus:border-red focus:bg-red/15 ${NAV_FOCUS_RING}`}
                    activeProps={{ "aria-current": "page" }}
                  >
                    <SeriesBadge slug={entry.slug} size="sm" className="!w-11 shrink-0" />
                    <span className="flex min-w-0 flex-col gap-1 leading-tight">
                      <span className="font-display text-[13px] font-bold uppercase tracking-wider text-white">
                        {entry.name}
                      </span>
                      <span className="text-[11px] text-white/70 group-hover:text-white/90">
                        {entry.description}
                      </span>
                      {count > 0 && (
                        <span className="mt-0.5 inline-flex w-fit items-center gap-1 rounded-full bg-red/25 px-2 py-0.5 font-display text-[10px] font-bold uppercase tracking-widest text-white/90 group-hover:bg-red group-hover:text-white">
                          {count} Products
                        </span>
                      )}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <div className="mt-1 flex items-center justify-between gap-4 border-t border-white/10 pt-3 sm:col-span-2 lg:col-span-4">
        <span className="font-display text-[11px] uppercase tracking-widest text-white/55">
          Every series is designed, sewn and welded in Texas.
        </span>
        <Link
          to="/series"
          ref={(el) => registerRef(el, idx)}
          role="menuitem"
          tabIndex={open ? 0 : -1}
          onClick={() => {
            trackSeriesCta(
              { slug: "all", name: "All Series", placement: "mega_menu" },
              "/series",
              "View All Series",
            );
            onSelect();
          }}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-md bg-red px-3 py-2 font-display text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-red/85 ${NAV_FOCUS_RING}`}
        >
          View All Series <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );


}


/**
 * Mobile/tablet nav item — collapsible dropdown for items with children.
 * Tap the label to navigate to the parent page; tap the chevron column to
 * expand sub-pages without leaving the menu. Tap targets meet 48px minimum
 * and the chevron column is visually separated for unambiguous tapping.
 */
function MobileNavItem({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = !!item.children && item.children.length > 0;
  const panelId = useId();

  return (
    <div className="border-b border-white/10">
      <div className="flex items-stretch">
        <Link
          to={item.to}
          className={`flex-1 ${mobileNavLink}`}
          activeProps={{ className: `flex-1 ${mobileNavLinkActive}`, "aria-current": "page" }}
          activeOptions={hasChildren ? undefined : { exact: true }}
          onClick={onNavigate}
        >
          {item.label}
        </Link>
        {hasChildren && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={expanded ? panelId : undefined}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${item.label} submenu`}
            onClick={() => setExpanded((v) => !v)}
            className={`flex h-auto min-h-[3rem] w-14 shrink-0 items-center justify-center border-l border-white/15 text-white transition-colors hover:bg-red active:bg-red-deep ${NAV_FOCUS_RING}`}
          >
            <ChevronDown
              className={`h-6 w-6 transition-transform ${expanded ? "rotate-180" : ""}`}
              aria-hidden="true"
              focusable="false"
            />
          </button>
        )}
      </div>
      {hasChildren && expanded && (
        <div
          id={panelId}
          className="flex flex-col bg-navy/60"
        >
          {item.children!.map((c) => (
            <MobileSubItem key={c.to} child={c} onNavigate={onNavigate} />
          ))}

        </div>
      )}
    </div>
  );
}

function MobileSubItem({ child, onNavigate }: { child: NavChild; onNavigate: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = !!child.children && child.children.length > 0;
  const panelId = useId();
  return (
    <div>
      <div className="flex items-stretch">
        <Link
          to={child.to}
          className={`flex-1 flex min-h-[3rem] items-center border-l-4 border-red/70 px-5 py-3 font-display text-sm uppercase tracking-wider text-white/95 transition-colors hover:bg-red hover:text-white active:bg-red-deep ${NAV_FOCUS_RING}`}
          activeProps={{
            className: `flex-1 flex min-h-[3rem] items-center border-l-4 border-red px-5 py-3 font-display text-sm uppercase tracking-wider bg-red text-white ${NAV_FOCUS_RING}`,
            "aria-current": "page",
          }}
          onClick={onNavigate}
        >
          {child.label}
        </Link>
        {hasChildren && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={expanded ? panelId : undefined}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${child.label} submenu`}
            onClick={() => setExpanded((v) => !v)}
            className={`flex h-auto min-h-[3rem] w-12 shrink-0 items-center justify-center border-l border-white/15 text-white transition-colors hover:bg-red active:bg-red-deep ${NAV_FOCUS_RING}`}
          >
            <ChevronDown className={`h-5 w-5 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true" focusable="false" />
          </button>
        )}
      </div>
      {hasChildren && expanded && (
        <div id={panelId} className="flex flex-col bg-navy/80">
          {child.children!.map((gc) => (
            <Link
              key={gc.to}
              to={gc.to}
              className={`flex min-h-[3rem] items-center border-l-4 border-red/50 pl-10 pr-5 py-3 font-display text-xs uppercase tracking-wider text-white/90 transition-colors hover:bg-red hover:text-white active:bg-red-deep ${NAV_FOCUS_RING}`}
              activeProps={{
                className: `flex min-h-[3rem] items-center border-l-4 border-red pl-10 pr-5 py-3 font-display text-xs uppercase tracking-wider bg-red text-white ${NAV_FOCUS_RING}`,
                "aria-current": "page",
              }}
              onClick={onNavigate}
            >
              {gc.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}


export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [fbVisited, setFbVisited] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Read the persisted FB-visited flag after mount so SSR markup stays stable
  // (no hydration mismatch from referencing localStorage during render).
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage.getItem(FB_VISITED_KEY) === "1") {
        setFbVisited(true);
      }
    } catch {
      // localStorage may be unavailable (private mode, blocked); ignore — link
      // simply renders in its base state, no functional impact.
    }
  }, []);

  const markFbVisited = () => {
    setFbVisited(true);
    try {
      window.localStorage.setItem(FB_VISITED_KEY, "1");
    } catch {
      /* see note above */
    }
  };

  // Close the mobile menu on Escape and return focus to the toggle for screen-reader continuity.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);
  return (
    <header className="relative z-50 bg-white text-navy-deep shadow-card">
      <div className="stripe-bar h-1.5 w-full" />
      <div className="bg-white">
        {/* Top row: logo + contact */}
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-2 sm:gap-4 sm:px-4 lg:px-8">
          <Link to="/" className={`flex min-w-0 items-center gap-2 sm:gap-3 ${TOPBAR_FOCUS_RING}`}>
            <Logo className="h-[4.5rem] w-auto shrink-0 sm:h-24 md:h-28 lg:h-44" loading="eager" fetchPriority="low" />
            <div className="hidden leading-tight lg:block">
              <div className="font-display text-base font-bold tracking-wide text-navy-deep lg:text-lg">TEXAS MADE SHADE & COVERS</div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-navy-deep">Shade • Outdoor Living • Fabrication</div>
              <div className="text-[13px] font-bold uppercase tracking-[0.12em] text-navy-deep font-tagline">One Source. Every Detail. Every Solution.</div>


            </div>
          </Link>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <a
              href="tel:+18303909588"
              data-cta-placement="header_desktop"
              className={`hidden items-center gap-2 bg-navy-deep px-5 py-3 font-display text-base font-extrabold uppercase tracking-wider text-white shadow-lg ring-1 ring-black/20 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)] transition motion-reduce:transition-none hover:bg-navy hover:shadow-xl lg:inline-flex ${TOPBAR_FOCUS_RING}`}
            >
              <Phone className="h-5 w-5" aria-hidden="true" focusable="false" /> +1 (830) 390-9588
            </a>
            <a
              href="sms:+18303909588"
              onClick={() => track("sms_click", { target: "8303909588", metadata: { placement: "header_desktop" } })}
              aria-label="Text Texas Made Shade at +1 (830) 390-9588"
              title="Text us"
              className={`hidden items-center gap-2 border-2 border-navy-deep bg-white px-4 py-[10px] font-display text-base font-bold uppercase tracking-wider text-navy-deep transition motion-reduce:transition-none hover:bg-navy-deep hover:text-white lg:inline-flex ${TOPBAR_FOCUS_RING}`}
            >
              <MessageSquare className="h-5 w-5" aria-hidden="true" focusable="false" /> Text
            </a>
            {/* Prominent estimate CTA — desktop */}
            <a
              href="/estimate-quotes#general"
              onClick={() => track("cta_click", { target: "header_estimate_cta", metadata: { placement: "header_desktop" } })}
              className={`hidden items-center gap-2 bg-red px-6 py-4 font-display text-lg font-extrabold uppercase tracking-wider text-white shadow-xl ring-2 ring-red-deep/40 transition motion-reduce:transition-none hover:bg-red-deep hover:shadow-2xl lg:inline-flex ${TOPBAR_FOCUS_RING}`}
            >
              Get a Quote <ArrowRight className="h-5 w-5" aria-hidden="true" focusable="false" />
            </a>
            {/* Prominent estimate CTA — mobile / tablet (compact but bold) */}
            <a
              href="/estimate-quotes#general"
              aria-label="Get a quote"
              onClick={() => track("cta_click", { target: "header_estimate_cta", metadata: { placement: "header_mobile" } })}
              className={`inline-flex items-center gap-1.5 bg-red px-3.5 py-2.5 font-display text-sm font-extrabold uppercase tracking-wider text-white shadow-lg ring-1 ring-red-deep/40 transition motion-reduce:transition-none active:scale-95 motion-reduce:active:scale-100 lg:hidden ${TOPBAR_FOCUS_RING}`}
            >
              Get a Quote
            </a>
            <a
              href="tel:+18303909588"
              aria-label="Call +1 (830) 390-9588"
              data-cta-placement="header_mobile_compact"
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full bg-navy-deep text-white shadow-md ring-1 ring-black/10 transition motion-reduce:transition-none active:scale-95 motion-reduce:active:scale-100 lg:hidden ${TOPBAR_FOCUS_RING}`}
            >
              <Phone className="h-5 w-5" aria-hidden="true" focusable="false" />
            </a>
            <button
              ref={toggleRef}
              type="button"
              className={`inline-flex h-10 w-10 items-center justify-center text-navy-deep lg:hidden ${TOPBAR_FOCUS_RING}`}
              onClick={() => setOpen(!open)}
              aria-label={open ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-haspopup="menu"
            >
              {open ? (
                <X className="h-6 w-6" aria-hidden="true" focusable="false" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" focusable="false" />
              )}
            </button>
          </div>
        </div>
        {/* Search bar — under the Get a Free Estimate CTA on all sizes */}
        <div className="border-t border-navy-deep/10 bg-white px-3 py-2 sm:px-4 lg:px-8">
          <div className="mx-auto flex max-w-7xl justify-stretch lg:justify-end">
            <SiteSearch className="w-full lg:w-64 xl:w-80" />
          </div>
        </div>
        {/* Inline desktop nav bar */}
        <nav aria-label="Primary" className="hidden border-t border-navy-deep/10 bg-navy-deep lg:block">
          <div className="mx-auto flex max-w-7xl items-center justify-center px-4 lg:px-8">
            {nav.map((n) => (
              n.children && n.children.length > 0 ? (
                <DesktopNavDropdown key={n.to} item={n} />
              ) : (
                <Link
                  key={n.to}
                  to={n.to}
                  className={desktopNavLink}
                  activeProps={{ className: desktopNavLinkActive, "aria-current": "page" }}
                  activeOptions={{ exact: true }}
                >
                  {n.label}
                </Link>
              )
            ))}
            {/*
              "Day to Day" Facebook link — desktop only, and only at viewports
              wide enough to fit it without crowding the primary nav.
              Hidden below 2xl (≈1536px) where it pushed nav items off the row.
            */}
            <div className="hidden 2xl:flex">
              <a
                href={FB_URL_DESKTOP}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="See our day-to-day work on Facebook (opens in a new tab)"
                title="See our Day to Day work on Facebook"
                aria-current={fbVisited ? "true" : undefined}
                data-visited={fbVisited ? "true" : undefined}
                onClick={() => {
                  track("facebook_click", { target: FB_URL_DESKTOP, metadata: { placement: "header_desktop" } });
                  markFbVisited();
                }}
                className={fbVisited ? desktopFbLinkActive : desktopFbLink}
              >
                <Facebook className="h-4 w-4 shrink-0 text-[#1877F2]" aria-hidden="true" focusable="false" />
                <span className="whitespace-nowrap">See our Day to Day work</span>
              </a>
            </div>
          </div>
        </nav>
        {/* Mobile menu — always rendered so aria-controls always resolves; hidden when closed. */}
        <nav
          id="mobile-nav"
          aria-label="Mobile primary"
          hidden={!open}
          className="border-t border-navy-deep/10 bg-navy-deep lg:hidden"
        >
          <div className="flex flex-col gap-y-1 px-4 py-4">
            <div className="mb-3">
              <SiteSearch />
            </div>
            {nav.map((n) => (
              <MobileNavItem key={n.to} item={n} onNavigate={() => setOpen(false)} />
            ))}
            <a
              href="tel:+18303909588"
              aria-label="Call Texas Made Shade at +1 (830) 390-9588"
              data-cta-placement="header_mobile_menu"
              className={`mt-5 inline-flex items-center justify-center gap-2 bg-red px-6 py-4 font-display text-lg font-bold uppercase tracking-wider text-white ${NAV_FOCUS_RING}`}
            >
              <Phone className="h-5 w-5" aria-hidden="true" focusable="false" /> Call +1 (830) 390-9588
            </a>
            <a
              href="sms:+18303909588"
              aria-label="Text Texas Made Shade at +1 (830) 390-9588"
              onClick={() => track("sms_click", { target: "8303909588", metadata: { placement: "header_mobile" } })}
              className={`mt-3 inline-flex items-center justify-center gap-2 border-2 border-white bg-navy-deep px-6 py-4 font-display text-lg font-bold uppercase tracking-wider text-white ${NAV_FOCUS_RING}`}
            >
              <MessageSquare className="h-5 w-5" aria-hidden="true" focusable="false" /> Text Us
            </a>
            <a
              href={FB_URL_MOBILE}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="See our day-to-day work on Facebook (opens in a new tab)"
              aria-current={fbVisited ? "true" : undefined}
              data-visited={fbVisited ? "true" : undefined}
              className={fbVisited ? mobileFbLinkActive : mobileFbLink}
              onClick={() => {
                track("facebook_click", { target: FB_URL_MOBILE, metadata: { placement: "header_mobile" } });
                markFbVisited();
                setOpen(false);
              }}
            >
              <Facebook className="h-4 w-4 text-[#1877F2]" aria-hidden="true" focusable="false" /> <span>See our Day to Day work</span>
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}
